import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

/** Everything the navigation decision depends on, injected so it stays testable. */
export interface NavigationContext {
  isAuthenticated: boolean
  /** The path is in the menu the current viewer is allowed to see. */
  isAllowedPath: (path: string) => boolean
  /** `admin` flag from getUser: full access to the admin panel. */
  isAdmin: boolean
  /** `editor` flag from getUser: the panel minus Menu and Users. */
  isEditor: boolean
  /** An admin has put the site in maintenance mode. */
  maintenanceActive: boolean
}

/**
 * Rejects absolute URLs and protocol-relative paths so `?redirect=` cannot be
 * abused to bounce a freshly logged-in user off-site.
 */
export function sanitizeRedirect(target: unknown): string | null {
  if (typeof target !== 'string' || target.length === 0) return null
  if (!target.startsWith('/')) return null
  if (target.startsWith('//')) return null
  return target
}

/**
 * Vera quando la rotta è del pannello ma chi la sta aprendo non deve nemmeno
 * sapere che esiste: utente loggato senza ruolo admin né editor.
 *
 * `resolveNavigation` lascia passare la navigazione in questi casi, e chi rende
 * la pagina (App.vue) disegna il 404 al posto della view **tenendo l'URL** — è
 * lo stesso trattamento che la rotta catch-all riserva a un path inesistente.
 * Rimbalzare sulla dashboard, invece, avrebbe confermato che il path c'è.
 *
 * Un anonimo non rientra qui: viene mandato al login come per qualsiasi altra
 * pagina, altrimenti un admin con l'URL nei preferiti non avrebbe modo di
 * entrare.
 */
export function rendersNotFound(to: RouteLocationNormalized, ctx: NavigationContext): boolean {
  if (!to.meta.requiresStaff && !to.meta.requiresAdmin) return false
  return ctx.isAuthenticated && !ctx.isAdmin && !ctx.isEditor
}

/**
 * Single source of truth for "may this navigation proceed?".
 *
 * Returns `true` to allow it, or a route location to redirect to.
 *
 * Note on anonymous visitors hitting an unknown path: `GET /menu` only returns
 * what the *current* viewer may see, so the client genuinely cannot tell
 * "exists but needs login" from "does not exist". We therefore send guests to
 * the login page carrying `redirect`, and re-evaluate once they are in — which
 * is what the spec asks for, and lands them on the page when they are allowed.
 */
export function resolveNavigation(
  to: RouteLocationNormalized,
  ctx: NavigationContext,
): RouteLocationRaw | true {
  const isStaff = ctx.isAdmin || ctx.isEditor

  // Maintenance mode. Staff work as usual — they are the ones who have to fix
  // whatever is being fixed and turn the site back on — and so do the few pages
  // marked as available (login, the notice itself). Everyone else is parked on
  // the notice page. The API enforces the same rule on its side: this is the
  // presentation half, not the protection.
  if (ctx.maintenanceActive && !isStaff && !to.meta.availableDuringMaintenance) {
    return { name: 'maintenance' }
  }

  // Conversely, the notice must not linger once the site is back up.
  if (!ctx.maintenanceActive && to.name === 'maintenance') {
    return { name: 'home' }
  }

  // Admin panel: reserved to admins and editors, with Menu and Users on top of
  // that reserved to admins alone.
  if (to.meta.requiresStaff || to.meta.requiresAdmin) {
    if (!ctx.isAuthenticated) {
      // Guests still go to the login page: an admin arriving on a bookmarked
      // panel URL must have a way in, and it is the same treatment every other
      // path gets for an anonymous visitor.
      return { name: 'login', query: { redirect: to.fullPath } }
    }
    // An ordinary user gets a 404 rendered in place (see `rendersNotFound`):
    // bouncing them to the dashboard would have told them the path exists.
    if (!isStaff) return true

    // An editor who lands on an admin-only section goes to the panel home
    // rather than out of the panel altogether: they already know the panel
    // exists, so there is nothing to hide from them — only somewhere better
    // to land.
    if (to.meta.requiresAdmin && !ctx.isAdmin) {
      return { name: 'admin-home' }
    }
    return true
  }

  // Pages reserved for anonymous visitors (login, register, landing, ...).
  if (to.meta.guestOnly && ctx.isAuthenticated) {
    return { name: 'home' }
  }

  // Static pages that require a session.
  if (to.meta.requiresAuth && !ctx.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Menu-driven pages: the catch-all route.
  if (to.name === 'dynamic') {
    if (ctx.isAllowedPath(to.path)) return true

    if (!ctx.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // Authenticated but not entitled (or the path does not exist at all):
    // let the component render the 404 so the URL is preserved.
    return true
  }

  return true
}
